class TttDeliveryEstimator extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[name="postcode"]');
    this.result = this.querySelector('[data-delivery-result]');
    this.config = JSON.parse(this.querySelector('[data-delivery-config]').textContent);
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.input.addEventListener('input', () => {
      this.input.value = this.input.value.replace(/\D/g, '').slice(0, 4);
      if (this.result.dataset.state === 'error') this.clearResult();
    });
  }

  onSubmit(event) {
    event.preventDefault();
    const postcode = this.input.value.trim();
    if (!/^\d{4}$/.test(postcode)) {
      this.renderError(this.config.invalid_postcode);
      return;
    }

    const zone = window.TttPostcodeZones?.[postcode];
    if (!zone) {
      this.renderError(this.config.unknown_postcode);
      return;
    }

    const dispatchDate = this.nextDispatchDate();
    const earliest = this.addWorkingDays(dispatchDate, Number(this.config[`${zone}_min`]));
    const latest = this.addWorkingDays(dispatchDate, Number(this.config[`${zone}_max`]));
    const rate = this.money(this.config[`${zone}_rate`]);
    const zoneLabel = this.config[`${zone}_label`];
    const express =
      zone === 'metro'
        ? `<p class="ttt-delivery-estimator__express"><strong>${this.escape(this.config.express_carrier)} is available.</strong> ${this.escape(this.config.express_note)}</p>`
        : `<p class="ttt-delivery-estimator__muted">${this.escape(this.config.express_unavailable)}</p>`;

    this.result.dataset.state = 'success';
    this.result.innerHTML = `
      <p class="ttt-delivery-estimator__eyebrow">${this.escape(zoneLabel)} · ${this.escape(postcode)}</p>
      <p class="ttt-delivery-estimator__date"><strong>${this.escape(this.dateRange(earliest, latest))}</strong></p>
      <p>${this.escape(this.config.standard_carrier)} standard shipping from <strong>${this.escape(rate)}</strong>. ${this.escape(this.config.rate_note)}</p>
      ${express}
    `;
  }

  clearResult() {
    this.result.dataset.state = '';
    this.result.replaceChildren();
  }

  renderError(message) {
    this.result.dataset.state = 'error';
    this.result.textContent = message;
  }

  nextDispatchDate() {
    const now = new Date();
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Manila',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      hourCycle: 'h23',
    }).formatToParts(now);
    const value = (type) => Number(parts.find((part) => part.type === type).value);
    const date = new Date(Date.UTC(value('year'), value('month') - 1, value('day'), 12));
    const hour = value('hour');
    const cutoff = Number(this.config.cutoff_hour);
    const day = date.getUTCDay();

    if (day === 0) return this.addCalendarDays(date, 1);
    if (day === 6 && hour >= cutoff) return this.addCalendarDays(date, 2);
    if (hour >= cutoff) {
      const tomorrow = this.addCalendarDays(date, 1);
      return tomorrow.getUTCDay() === 0 ? this.addCalendarDays(tomorrow, 1) : tomorrow;
    }
    return date;
  }

  addWorkingDays(start, numberOfDays) {
    let date = new Date(start);
    let added = 0;
    while (added < numberOfDays) {
      date = this.addCalendarDays(date, 1);
      if (date.getUTCDay() !== 0) added += 1;
    }
    return date;
  }

  addCalendarDays(start, days) {
    const date = new Date(start);
    date.setUTCDate(date.getUTCDate() + days);
    return date;
  }

  dateRange(earliest, latest) {
    const formatter = new Intl.DateTimeFormat('en-PH', {
      timeZone: 'UTC',
      month: 'short',
      day: 'numeric',
    });
    return `${this.config.arrives_label} ${formatter.format(earliest)}–${formatter.format(latest)}`;
  }

  money(value) {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      maximumFractionDigits: 0,
    }).format(Number(value));
  }

  escape(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }
}

if (!customElements.get('ttt-delivery-estimator')) {
  customElements.define('ttt-delivery-estimator', TttDeliveryEstimator);
}
